/**
 * Admin — add vendor (design SimpleCreate): name, category, phone, notes.
 * Wired: POST /api/admin/vendors. Port of screens-admin.jsx CreateVendor.
 */
import React from 'react';

import { space } from '@bero/tokens';
import { InfoNote } from '@/components/gate/bits';
import { AdminScreenShell, goBack } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { BottomBar } from '@/features/community/bits';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { Button, Field, IconButton, Input, Screen } from '@/ui';

export default function CreateVendorScreen() {
  const { colors } = useTheme();
  const [name, setName] = React.useState('');
  const [category, setCategory] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [note, setNote] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);

  const canSave = name.trim().length > 0 && category.trim().length > 0;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setNotice(null);
    try {
      await api.post<unknown>('/api/admin/vendors', {
        name: name.trim(),
        category: category.trim(),
        phone: phone.trim() || undefined,
        note: note.trim() || undefined,
      });
      goBack();
    } catch {
      setNotice('Could not add the vendor right now. Try again in a bit.');
      setSaving(false);
    }
  };

  return (
    <AdminScreenShell
      title="Add vendor"
      right={
        <IconButton label="Discard" onPress={goBack}>
          <Icon name="x" size={20} color={colors.textPrimary} />
        </IconButton>
      }
    >
      <Screen padded style={{ paddingTop: space.s4, gap: space.s4 }}>
        <Field label="Name">
          <Input value={name} onChangeText={setName} placeholder="Vendor name" />
        </Field>
        <Field label="Category">
          <Input value={category} onChangeText={setCategory} placeholder="Electrician, Plumbing…" />
        </Field>
        <Field label="Phone">
          <Input value={phone} onChangeText={setPhone} placeholder="+91" keyboardType="phone-pad" />
        </Field>
        <Field label="Notes">
          <Input
            value={note}
            onChangeText={setNote}
            placeholder="Why residents trust them"
            multiline
            style={{ minHeight: 96, textAlignVertical: 'top' }}
          />
        </Field>
        {notice ? <InfoNote icon="info" tint={colors.danger500}>{notice}</InfoNote> : null}
      </Screen>
      <BottomBar>
        <Button title="Add vendor" size="lg" disabled={!canSave} loading={saving} onPress={() => { void save(); }} />
      </BottomBar>
    </AdminScreenShell>
  );
}
